import { useEffect, useState } from 'react'
import { Box, Button, Collapse, Divider, Stack, Switch, Typography } from '@mui/material'
import CookieRoundedIcon from '@mui/icons-material/CookieRounded'
import { useTranslation } from 'react-i18next'
import { OPTIONAL_CATEGORIES, readConsent, writeConsent } from '@/lib/cookieConsent'

/**
 * Cookie consent banner.
 *
 * FaceWise sets no analytics or marketing cookies today — the only browser
 * storage is the session, the language and the selected view, all strictly
 * necessary. The banner still offers the full choice, and the choice is stored
 * for real (see `@/lib/cookieConsent`), so the day a non-essential script is
 * added it is already gated behind it. Nothing here claims that such cookies
 * are in use: the copy is deliberately written in the future tense.
 *
 * Mounted once at the app root next to {@link OfflineBanner}, so it also covers
 * the public respondent journey and the login screen.
 */
export default function CookieNotice() {
  const { t } = useTranslation('common')
  // Hidden until we know there is no stored decision; a short delay keeps the
  // bar from sliding in over a still-painting dashboard.
  const [visible, setVisible] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [analytics, setAnalytics] = useState(false)
  const [marketing, setMarketing] = useState(false)

  useEffect(() => {
    if (readConsent()) return
    const timer = window.setTimeout(() => setVisible(true), 600)
    return () => window.clearTimeout(timer)
  }, [])

  const decide = (choice: { analytics: boolean; marketing: boolean }) => {
    writeConsent(choice)
    setVisible(false)
  }

  const optional = { analytics, marketing }
  const setOptional = { analytics: setAnalytics, marketing: setMarketing }

  if (!visible) return null

  return (
    <Box
      role="dialog"
      aria-modal={false}
      aria-labelledby="cookie-notice-title"
      sx={(theme) => ({
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: theme.zIndex.snackbar,
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 1.25 },
        borderTop: '1px solid rgba(255,255,255,.10)',
        background: theme.affexy.gradients.ctaCard,
        boxShadow: theme.affexy.shadows.overlay,
        color: theme.affexy.colors.white,
        animation: 'cookieNoticeIn .32s ease both',
        '@keyframes cookieNoticeIn': {
          from: { transform: 'translateY(100%)' },
          to: { transform: 'none' },
        },
      })}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Collapse in={settingsOpen} unmountOnExit>
          <Stack spacing={1} sx={{ pb: 1.5 }}>
            {/* Necessary is listed like the others but cannot be turned off. */}
            <CategoryRow
              name={t('cookies.categories.necessary.name')}
              description={t('cookies.categories.necessary.description')}
              checked
              disabled
              hint={t('cookies.alwaysOn')}
            />
            {OPTIONAL_CATEGORIES.map((category) => (
              <CategoryRow
                key={category}
                name={t(`cookies.categories.${category}.name`)}
                description={t(`cookies.categories.${category}.description`)}
                checked={optional[category]}
                onChange={setOptional[category]}
              />
            ))}
          </Stack>
          <Divider sx={{ borderColor: 'rgba(255,255,255,.10)', mb: 1.5 }} />
        </Collapse>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.5, sm: 3 }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
            <CookieRoundedIcon
              sx={(theme) => ({ flexShrink: 0, fontSize: 22, color: theme.affexy.colors.coral })}
            />
            <Typography
              id="cookie-notice-title"
              sx={(theme) => ({
                fontFamily: theme.affexy.fonts.body,
                fontSize: 13.5,
                lineHeight: 1.45,
                color: 'rgba(255,255,255,.72)',
              })}
            >
              <Box
                component="span"
                sx={(theme) => ({
                  fontFamily: theme.affexy.fonts.heading,
                  fontWeight: 700,
                  color: theme.affexy.colors.white,
                  mr: 0.75,
                })}
              >
                {t('cookies.title')}
              </Box>
              {t('cookies.body')}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={{ xs: 'stretch', sm: 'flex-end' }}
            sx={{ flexShrink: 0 }}
          >
            <BarButton onClick={() => setSettingsOpen((open) => !open)} variant="quiet">
              {t('cookies.settings')}
            </BarButton>
            {settingsOpen && (
              <BarButton onClick={() => decide({ analytics, marketing })} variant="outline">
                {t('cookies.save')}
              </BarButton>
            )}
            <BarButton onClick={() => decide({ analytics: false, marketing: false })} variant="outline">
              {t('cookies.reject')}
            </BarButton>
            <BarButton onClick={() => decide({ analytics: true, marketing: true })} variant="solid">
              {t('cookies.accept')}
            </BarButton>
          </Stack>
        </Stack>
      </Box>
    </Box>
  )
}

/** One switchable cookie category inside the expanded settings. */
function CategoryRow({
  name,
  description,
  checked,
  disabled,
  hint,
  onChange,
}: {
  name: string
  description: string
  checked: boolean
  disabled?: boolean
  hint?: string
  onChange?: (value: boolean) => void
}) {
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
          <Typography
            sx={(theme) => ({
              fontFamily: theme.affexy.fonts.heading,
              fontWeight: 700,
              fontSize: 13.5,
              color: theme.affexy.colors.white,
            })}
          >
            {name}
          </Typography>
          {hint && (
            <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,.40)' }}>{hint}</Typography>
          )}
        </Stack>
        <Typography sx={{ fontSize: 12.5, lineHeight: 1.45, color: 'rgba(255,255,255,.56)' }}>
          {description}
        </Typography>
      </Box>
      <Switch
        size="small"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange?.(event.target.checked)}
        inputProps={{ 'aria-label': name }}
        sx={(theme) => ({
          mt: 0.25,
          '& .Mui-checked': { color: theme.affexy.colors.coral },
          '& .Mui-checked + .MuiSwitch-track': { backgroundColor: theme.affexy.colors.coral },
          '& .MuiSwitch-track': { backgroundColor: 'rgba(255,255,255,.24)' },
        })}
      />
    </Stack>
  )
}

/** The three button weights used in the bar: quiet link, outline, solid coral. */
function BarButton({
  children,
  onClick,
  variant,
}: {
  children: React.ReactNode
  onClick: () => void
  variant: 'quiet' | 'outline' | 'solid'
}) {
  return (
    <Button
      onClick={onClick}
      disableElevation
      sx={(theme) => ({
        flex: { xs: 1, sm: '0 0 auto' },
        borderRadius: `${theme.affexy.radii.pill}px`,
        px: { xs: 1.5, sm: 2.5 },
        py: 0.75,
        fontFamily: theme.affexy.fonts.body,
        fontSize: 13.5,
        fontWeight: 700,
        textTransform: 'none',
        whiteSpace: 'nowrap',
        minWidth: 0,
        ...(variant === 'solid' && {
          color: theme.affexy.colors.white,
          bgcolor: theme.affexy.colors.coral,
          '&:hover': { bgcolor: theme.affexy.colors.coralHover },
        }),
        ...(variant === 'outline' && {
          color: theme.affexy.colors.white,
          border: '1px solid rgba(255,255,255,.28)',
          '&:hover': { borderColor: 'rgba(255,255,255,.56)', bgcolor: 'rgba(255,255,255,.06)' },
        }),
        ...(variant === 'quiet' && {
          color: 'rgba(255,255,255,.64)',
          fontWeight: 500,
          '&:hover': { color: theme.affexy.colors.white, bgcolor: 'transparent' },
        }),
        '&:focus-visible': { boxShadow: theme.affexy.shadows.focus },
      })}
    >
      {children}
    </Button>
  )
}
